/**
 * The errors this browser tab has shown, newest first, for the Activity Hub's
 * Errors tab. An error toast is gone after a few seconds; this is where the user
 * finds it again, copies it, or sends it to support.
 *
 * Kept in sessionStorage: it survives a reload (the usual reaction to an error)
 * but not the tab, and it is cleared on sign-out so the next person on a shared
 * machine never sees it. Nothing leaves the browser.
 */
export interface ErrorLogEntry {
	id: string;
	/** Epoch ms of the most recent occurrence. */
	at: number;
	message: string;
	description?: string;
	/** The route the user was on when it happened. */
	path: string;
	/** How many times it was repeated within {@link REPEAT_WINDOW_MS}. */
	count: number;
}

const STORAGE_KEY = "oneai:error-log";
export const ERROR_LOG_LIMIT = 50;
/**
 * A toast re-raised in place (`id: sync-error-<space>`) or a retry loop fails
 * the same way in quick succession; those become one row with a count.
 */
export const REPEAT_WINDOW_MS = 5000;

const EMPTY: readonly ErrorLogEntry[] = [];

// Loaded on first read rather than at import, so the module can be imported
// where sessionStorage does not exist yet.
let entries: readonly ErrorLogEntry[] | null = null;
const listeners = new Set<() => void>();

function isEntry(value: unknown): value is ErrorLogEntry {
	if (value === null || typeof value !== "object") {
		return false;
	}
	const v = value as Record<string, unknown>;
	return (
		typeof v.id === "string" &&
		typeof v.at === "number" &&
		typeof v.message === "string" &&
		(v.description === undefined || typeof v.description === "string") &&
		typeof v.path === "string" &&
		typeof v.count === "number"
	);
}

function load(): readonly ErrorLogEntry[] {
	try {
		const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
		const parsed: unknown = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed)
			? parsed.filter(isEntry).slice(0, ERROR_LOG_LIMIT)
			: EMPTY;
	} catch {
		// Storage disabled or a corrupt value: the log starts empty.
		return EMPTY;
	}
}

function commit(next: readonly ErrorLogEntry[]): void {
	entries = next;
	try {
		if (next.length === 0) {
			globalThis.sessionStorage?.removeItem(STORAGE_KEY);
		} else {
			globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
		}
	} catch {
		// Quota or disabled storage: the log still works for this page load.
	}
	for (const fn of listeners) {
		fn();
	}
}

export function getErrorLogSnapshot(): readonly ErrorLogEntry[] {
	entries ??= load();
	return entries;
}

export function subscribeErrorLog(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

export function recordError(error: {
	message: string;
	description?: string;
}): void {
	const message = error.message.trim();
	if (!message) {
		return;
	}
	const description = error.description?.trim() || undefined;
	const now = Date.now();
	const current = getErrorLogSnapshot();
	const [latest, ...older] = current;
	if (
		latest !== undefined &&
		latest.message === message &&
		latest.description === description &&
		now - latest.at < REPEAT_WINDOW_MS
	) {
		commit([{ ...latest, at: now, count: latest.count + 1 }, ...older]);
		return;
	}
	const entry: ErrorLogEntry = {
		id: crypto.randomUUID(),
		at: now,
		message,
		description,
		path: globalThis.location?.pathname ?? "",
		count: 1,
	};
	commit([entry, ...current].slice(0, ERROR_LOG_LIMIT));
}

export function clearErrorLog(): void {
	if (getErrorLogSnapshot().length > 0) {
		commit(EMPTY);
	}
}

/** One entry as plain text, for the clipboard and for a support message. */
export function formatErrorLogEntry(entry: ErrorLogEntry): string {
	const head = `[${new Date(entry.at).toISOString()}] ${entry.path}${
		entry.count > 1 ? ` (×${entry.count})` : ""
	}`;
	return [head, entry.message, entry.description]
		.filter((line) => line !== undefined)
		.join("\n");
}
